import { useEffect, useRef } from "react";
import { useUser } from "@clerk/react";
import { getPostHog, identifyTraitsFromClerkUser } from "../lib/posthog";

/**
 * Keeps PostHog's identity in sync with Clerk auth so events attribute to a
 * real person:
 *   - on sign-in  → `posthog.identify(clerkUserId, { email, name })`
 *   - on sign-out → `posthog.reset()` (only after a prior identify, so we never
 *     churn the anonymous distinct_id of a visitor who is just browsing the
 *     funnel signed-out — that would fragment anonymous sessions on every load)
 *
 * Renders nothing. Must be mounted inside <ClerkProvider> (it calls useUser).
 * A no-op when analytics is disabled (getPostHog() === null, e.g. no key).
 */
export function PostHogIdentify() {
  const { isLoaded, isSignedIn, user } = useUser();
  const hasIdentified = useRef(false);

  useEffect(() => {
    const ph = getPostHog();
    if (!ph || !isLoaded) return;
    if (isSignedIn && user) {
      ph.identify(user.id, identifyTraitsFromClerkUser(user));
      hasIdentified.current = true;
    } else if (hasIdentified.current) {
      ph.reset();
      hasIdentified.current = false;
    }
    // Re-run when identity or the identifying traits change.
  }, [isLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress, user?.fullName]);

  return null;
}
